export default function activateSticky() {
    const STICKY_CLASS = 'sticky'
    const DEFAULT_OFFSET_TOP = 32
    const DEBOUNCE_DELAY = 100
    const LG_BREAKPOINT = 1024 // Matches $breakpoints in _variables.scss
    const stickies = []

    // Check if we're above the lg breakpoint
    function isAboveLg() {
        return window.innerWidth >= LG_BREAKPOINT
    }

    // Reset sticky element to normal state
    function resetSticky(sticky) {
        sticky.el.style.position = ''
        sticky.el.style.top = ''
        sticky.el.style.left = ''
        sticky.el.style.right = ''
        sticky.el.style.width = ''
        sticky.el.style.zIndex = ''
    }

    // Disable all sticky behavior
    function disableSticky() {
        stickies.forEach((sticky) => {
            resetSticky(sticky)
            sticky.state = 'normal'
        })
    }

    // Initialize sticky elements
    const stickyElements = document.querySelectorAll(`.${STICKY_CLASS}`)
    
    // Early return if no sticky elements exist
    if (stickyElements.length === 0) {
        return
    }

    stickyElements.forEach((el) => {
        const offsetTopAttr = el.getAttribute('data-sticky-top')
        const adjacentElementSelector = el.getAttribute('data-adjacent-element')
        const adjacentElement = document.querySelector(adjacentElementSelector)
        const topOffset = offsetTopAttr
            ? parseInt(offsetTopAttr, 10)
            : DEFAULT_OFFSET_TOP
        const parent = el.parentElement

        // Ensure parent has position: relative
        parent.style.position = 'relative'

        // Ensure the sticky element is isolated from other elements
        el.style.isolation = 'isolate'

        // Create placeholder element to capture element size
        const placeholder = document.createElement('div')
        placeholder.style.position = 'absolute'
        placeholder.style.top = '0'
        placeholder.style.left = '0'
        placeholder.style.right = '0'
        placeholder.style.height = `${el.offsetHeight}px`
        placeholder.style.pointerEvents = 'none'
        placeholder.style.visibility = 'hidden'

        parent.insertBefore(placeholder, el.nextSibling)

        stickies.push({
            el,
            parent,
            adjacentElement,
            placeholder,
            top: topOffset,
            offsetTop: null,
            parentPadding: {},
            state: 'normal', // 'normal', 'fixed', 'bottomed'
        })
    })

    function calculatePositions() {
        // Don't calculate positions if below lg breakpoint
        if (!isAboveLg()) {
            return
        }

        stickies.forEach((sticky) => {
            // Temporarily reset element to normal state to get accurate measurements
            const wasSticky = sticky.state !== 'normal'
            if (wasSticky) {
                resetSticky(sticky)
            }

            // Update placeholder to current element position and size
            const rect = sticky.el.getBoundingClientRect()
            sticky.offsetTop = rect.top + window.scrollY
            sticky.placeholder.style.height = `${rect.height}px`
            sticky.placeholder.style.width = `${rect.width}px`

            // Always capture current styles and padding
            const parentComputedStyle = window.getComputedStyle(sticky.parent)

            sticky.parentPadding = {
                top: parseInt(parentComputedStyle.paddingTop, 10) || 0,
                bottom: parseInt(parentComputedStyle.paddingBottom, 10) || 0,
                left: parseInt(parentComputedStyle.paddingLeft, 10) || 0,
                right: parseInt(parentComputedStyle.paddingRight, 10) || 0,
            }
        })
    }

    function updateStickies() {
        // Don't update stickies if below lg breakpoint
        if (!isAboveLg()) {
            return
        }

        const scrollY = window.scrollY

        stickies.forEach((sticky) => {
            const { el, parent, adjacentElement, top, offsetTop, parentPadding } = sticky

            const parentRect = parent.getBoundingClientRect()
            const parentTop = parentRect.top + window.scrollY
            const parentBottom = parentTop + parentRect.height
            const elementRect = el.getBoundingClientRect()
            const elementHeight = elementRect.height

            // If the adjacent element is taller than the sticky element, disable sticky behavior
            if (adjacentElement) {
                const adjacentElementRect = adjacentElement.getBoundingClientRect()
                const adjacentElementHeight = adjacentElementRect.height

                if (elementHeight >= adjacentElementHeight) {
                    disableSticky()
                    return
                }
            }

            // Calculate boundaries
            const stickyBottom = scrollY + top + elementHeight
            const isPastTop = scrollY + top >= offsetTop
            const isPastBottom =
                stickyBottom >= parentBottom - parentPadding.bottom
            const isBackToTop = scrollY + top < offsetTop

            // Calculate width for sticky positioning
            const stickyWidth = `${
                parentRect.width - parentPadding.left - parentPadding.right
            }px`

            if (isBackToTop) {
                sticky.state = 'normal'
            } else if (isPastBottom) {
                sticky.state = 'bottomed'
            } else if (isPastTop) {
                sticky.state = 'fixed'
            }

            if (sticky.state === 'normal') {
                // Return to normal position
                resetSticky(sticky)
            } else if (sticky.state === 'fixed') {
                // Fixed position at top
                el.style.position = 'fixed'
                el.style.top = `${top}px`
                el.style.left = `${parentRect.left + parentPadding.left}px`
                el.style.right = ''
                el.style.width = stickyWidth
                el.style.zIndex = '99'
            } else if (sticky.state === 'bottomed') {
                // Absolute position at bottom of parent
                el.style.position = 'absolute'
                el.style.top = `${
                    parentRect.height - elementHeight - parentPadding.bottom
                }px`
                el.style.left = `${parentPadding.left}px`
                el.style.right = `${parentPadding.right}px`
                el.style.width = stickyWidth
                el.style.zIndex = '99'
            }
        })
    }

    // Resize handler
    let resizeTimeout
    function handleResize() {
        clearTimeout(resizeTimeout)
        resizeTimeout = setTimeout(() => {
            // If we're going below lg breakpoint, disable sticky behavior
            if (!isAboveLg()) {
                disableSticky()
                return
            }

            // If we're going above lg breakpoint, reinitialize
            calculatePositions()
            updateStickies()
        }, DEBOUNCE_DELAY)
    }

    // Event listeners
    window.addEventListener('scroll', () => updateStickies())
    window.addEventListener('resize', handleResize)

    // Initial setup - only if above lg breakpoint
    if (isAboveLg()) {
        calculatePositions()
        updateStickies()
    }
}
